import { StateOption, normalizeCountry, statesFor } from '../_data/country-states';

/**
 * The shipping zone the welcome question saves. It mirrors, field for field, what Settings > Shipping
 * (shipping-rules-v2 `prepareShippingData`) sends to webstore/shipping, so a zone saved here opens and
 * edits there exactly like one made by hand.
 */
export type ShippingChoice = 'flat' | 'free' | 'calc';

/** USPS services switched on for Calculated Rates (the screen requires at least one). */
export const DEFAULT_USPS_SERVICES = ['priority_mail', 'ground_advantage'];

const SEPARATOR = ' || ';

/** "Rs." for a Pakistani business (PKR), "$" otherwise. */
export function currencySymbolFor(country: string | null | undefined, currencyCode?: string | null): string {
  return normalizeCountry(country) === 'PK' || String(currencyCode || '').toUpperCase() === 'PKR' ? 'Rs.' : '$';
}

/** The shipping choices a country can use: Calculated Rates is USPS, so US only. */
export function shippingChoicesFor(country: string | null | undefined): ShippingChoice[] {
  return normalizeCountry(country) === 'PK' ? ['flat', 'free'] : ['flat', 'free', 'calc'];
}

/** A usable 5-digit US origin ZIP (the signup placeholder 00000 is not). */
export function hasOriginZip(zip: string | null | undefined): boolean {
  const value = String(zip || '').trim();
  return /^\d{5}/.test(value) && value.slice(0, 5) !== '00000';
}

export interface ShippingBuildInput {
  choice: ShippingChoice;
  /** Required (> 0) for flat. */
  flatCost?: number;
  /** The business-setup record (address, city, state, zip, phone, country). */
  setup: any;
  /** The zones already saved; the first is updated in place and the rest are kept untouched. */
  existingZones?: any[];
}

export function shippingCostError(choice: ShippingChoice, flatCost: number | null | undefined): string | null {
  return choice === 'flat' && !(Number(flatCost) > 0) ? 'Shipping cost must be greater than 0.' : null;
}

function field(setup: any, name: string): any {
  if (!setup) return undefined;
  return setup[name] ?? setup[name.charAt(0).toUpperCase() + name.slice(1)];
}

export function buildShippingPayload(input: ShippingBuildInput): { zones: any[] } {
  const { choice, setup } = input;
  const country = normalizeCountry(field(setup, 'country'));
  const regionKey = country === 'PK' ? 'PK' : 'USA';
  const codes = statesFor(country).map((s: StateOption) => s.code);
  const existing = Array.isArray(input.existingZones) ? input.existingZones : [];
  const first = existing[0] || null;

  const address = String(field(setup, 'address') || '').split(SEPARATOR)[0].trim();
  const zip = String(field(setup, 'zip') || '');
  const isLive = choice === 'calc';

  const zone: any = {
    isEnabled: true,
    zoneName: first?.zoneName || 'Default Zone',
    shippingtype: choice === 'flat' ? 1 : choice === 'free' ? 2 : 3,
    country,
    address,
    phone: field(setup, 'phone') || '',
    city: field(setup, 'city') || '',
    zipCode: zip,
    state: field(setup, 'state') || '',
    countries: [regionKey],
    states: { [regionKey]: codes },
    isSaved: true,
    flatRate: {
      isEnableFlat: choice === 'flat',
      rate: choice === 'flat' ? Number(input.flatCost) : 0,
      isTaxIncluded: true,
      taxRate: 0
    },
    storePickUp: {
      isEnablePickup: first?.storePickUp?.isEnablePickup === true,
      pickupCost: first?.storePickUp?.isEnablePickup === true ? Number(first?.storePickUp?.pickupCost) || 0 : 0,
      isTaxIncluded: true,
      taxRate: 0
    },
    usps: {
      isEnabledUsps: isLive,
      postcode: zip,
      enabledServices: isLive ? DEFAULT_USPS_SERVICES : []
    }
  };

  // Keep the ids of the zone being updated, so the save edits it instead of adding another.
  if (first?.id) zone.id = first.id;
  if (first?.flatRate?.id) zone.flatRate.id = first.flatRate.id;
  if (first?.storePickUp?.id) zone.storePickUp.id = first.storePickUp.id;
  if (first?.usps?.id) zone.usps.id = first.usps.id;
  if (first?.usps?.userID) zone.usps.userID = first.usps.userID;

  return { zones: [zone, ...existing.slice(1)] };
}
